import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import { customerService } from '@/services/customer.service';
import type { Customer } from '@/services/customer.service';
import { useHasPermission } from '@/hooks/use-permissions';

/**
 * D-104 (Stage D) — the customer information card shown when the Equipment page
 * is scoped to one organisation.
 *
 * Two rules govern what appears here:
 *
 *  1. The REGISTERED ORGANISATION NAME is the identity (D-101). The contact
 *     person is secondary information, never the heading.
 *  2. Authentication data is not customer information. `username`,
 *     `mustChangePassword` and `passwordChangedAt` exist on the model and are
 *     deliberately NOT rendered.
 *
 * Every field is optional in the schema, so each is omitted rather than shown
 * as an empty row — a card of blank labels tells the operator nothing.
 */

interface Props {
  customerId: string;
}

const organisationOf = (c: Customer) => c.organizationName ?? c.name;

function formatAddress(c: Customer): string | null {
  const parts = [
    c.addressLine1 ?? c.address,
    c.addressLine2,
    c.city,
    c.state,
    c.postalCode,
    c.country,
  ].filter((p): p is string => Boolean(p && p.trim()));
  return parts.length > 0 ? parts.join(', ') : null;
}

function Detail({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 truncate text-sm text-foreground">{value}</dd>
    </div>
  );
}

export function CustomerInfoCard({ customerId }: Props) {
  const can = useHasPermission();
  const allowed = can('customer:view');

  const { data: customer, isError } = useQuery({
    queryKey: ['customer-info-card', customerId],
    queryFn: () => customerService.get(customerId),
    enabled: allowed,
    retry: false,
  });

  // Without customer:view the card is simply absent — the equipment list still
  // works, and no partial or placeholder identity is invented.
  if (!allowed || isError || !customer) return null;

  const address = formatAddress(customer);

  return (
    <section
      aria-label="Customer information"
      className="rounded-lg border border-border bg-card p-4"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <FontAwesomeIcon icon={faBuilding} className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-semibold text-foreground">
            {organisationOf(customer)}
          </h2>
          {customer.primaryContactName ? (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              Contact: {customer.primaryContactName}
            </p>
          ) : null}

          <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
            <Detail label="Email" value={customer.email} />
            <Detail label="Phone" value={customer.phone} />
            <Detail label="Alternate Phone" value={customer.alternatePhone} />
            <Detail label="GST Number" value={customer.gstNumber} />
            <Detail label="Address" value={address} />
          </dl>
        </div>
      </div>
    </section>
  );
}
